import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Upload, RefreshCw, Edit2, X, Loader2, Shield, GraduationCap, Users as UsersIcon, ExternalLink, UserX, UserCheck } from 'lucide-react'
import { usersApi, classesApi } from '../api/services.js'
import { schoolsApi } from '../api/services.js'
import { useAuth } from '../hooks/useAuth.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'
import PageHeader from '../components/PageHeader.jsx'

const roleOptions = [
  { value: 'all', label: 'All', badge: 'bg-gray-100 text-gray-700' },
  { value: 'teacher', label: 'Teacher', badge: 'bg-cyan-50 text-cyan-700' },
  { value: 'student', label: 'Student', badge: 'bg-green-50 text-green-700' },
  { value: 'parent', label: 'Parent', badge: 'bg-amber-50 text-amber-700' },
  { value: 'admin', label: 'Admin', badge: 'bg-violet-50 text-violet-700' },
]

const emptyForm = {
  role: 'student',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  mobile: '',
  gender: 'Male',
  dob: '',
  password: '',
  admissionNo: '',
  classId: '',
}

const getUserId = (user) => user?._id || user?.id || user?.userId
const getUserName = (user) => user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '—'
const getRoleMeta = (role) => roleOptions.find((item) => item.value === role) || roleOptions[0]
const getClassLabel = (user) => user?.classId?.name ? `${user.classId.name}${user.classId.section ? ` — ${user.classId.section}` : ''}` : '—'

export default function UsersPage() {
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const schoolId = authUser?.schoolId?._id || authUser?.schoolId || authUser?.school?._id || authUser?.school || ''

  const [users, setUsers] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [statusTarget, setStatusTarget] = useState(null)
  const [togglingStatus, setTogglingStatus] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, classesRes] = await Promise.all([
        usersApi.getAll({ accountStatus: 'active', limit: 2000 }),
        classesApi.getAll(),
      ])
      const all = Array.isArray(usersRes) ? usersRes : []
      setUsers(all.filter((u) => u.accountStatus !== 'inactive'))
      setClasses(Array.isArray(classesRes) ? classesRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase()
    return users.filter((user) => {
      const matchesRole = roleFilter === 'all' || user.role === roleFilter
      const matchesSearch = !query || [
        getUserName(user),
        user.email,
        user.mobile,
        user.admissionNo,
      ].filter(Boolean).some((value) => value.toLowerCase().includes(query))
      return matchesRole && matchesSearch
    })
  }, [roleFilter, search, users])

  const roleCounts = useMemo(() => {
    return roleOptions.reduce((acc, role) => {
      acc[role.value] = role.value === 'all'
        ? users.length
        : users.filter((user) => user.role === role.value).length
      return acc
    }, {})
  }, [users])

  const resetModal = () => {
    setModalOpen(false)
    setEditingUser(null)
    setForm(emptyForm)
  }

  const openCreate = () => {
    setEditingUser(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (user) => {
    setEditingUser(user)
    setForm({
      role: user.role || 'student',
      firstName: user.firstName || '',
      middleName: user.middleName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      mobile: user.mobile || '',
      gender: user.gender || 'Male',
      dob: user.dob ? user.dob.slice(0, 10) : '',
      password: '',
      admissionNo: user.admissionNo || '',
      classId: user.classId?._id || user.classId || '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    // Validation
    if (!form.firstName.trim()) { toast.error('First name is required'); return }
    if (!form.lastName.trim())  { toast.error('Last name is required');  return }
    if (!form.email.trim()) {
      toast.error('Email is required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Enter a valid email address')
      return
    }
    if (!form.mobile.trim()) {
      toast.error('Mobile number is required')
      return
    }
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) {
      toast.error('Enter a valid 10-digit Indian mobile number')
      return
    }
    if (!editingUser) {
      if (!form.password.trim()) { toast.error('Password is required'); return }
      if (form.password.trim().length < 6) { toast.error('Password must be at least 6 characters'); return }
      if (!form.dob) { toast.error('Date of birth is required'); return }
    }
    if (form.role === 'student' && !editingUser && !form.classId) {
      toast.error('Class is required for students')
      return
    }

    try {
      setSaving(true)
      if (editingUser) {
        await usersApi.update(getUserId(editingUser), {
          firstName: form.firstName.trim(),
          middleName: form.middleName.trim() || undefined,
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          mobile: form.mobile.trim(),
          gender: form.gender.toLowerCase(),
          dob: form.dob || undefined,
        })
        toast.success('User updated')
      } else if (form.role === 'admin') {
        // Admin creation uses the dedicated schools/admin endpoint
        await schoolsApi.createAdmin({
          schoolId: schoolId || undefined,
          firstName: form.firstName.trim(),
          middleName: form.middleName.trim() || undefined,
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          mobile: form.mobile.trim(),
          password: form.password.trim(),
          dob: form.dob,
        })
        toast.success('Admin account created')
      } else {
        await usersApi.create({
          firstName: form.firstName.trim(),
          middleName: form.middleName.trim() || undefined,
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          mobile: form.mobile.trim(),
          gender: form.gender.toLowerCase(),
          dob: form.dob,
          password: form.password.trim(),
          role: form.role,
          ...(form.role === 'student'
            ? {
                admissionNo: form.admissionNo.trim() || undefined,
                classId: form.classId || undefined,
              }
            : {}),
        })
        toast.success('User created')
      }
      resetModal()
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save user')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!statusTarget) return
    const nextStatus = statusTarget.currentStatus === 'inactive' ? 'active' : 'inactive'
    try {
      setTogglingStatus(true)
      const updated = await usersApi.updateAccountStatus(statusTarget.id, nextStatus)
      setUsers((prev) => prev.map((u) =>
        getUserId(u) === statusTarget.id ? { ...u, ...updated, accountStatus: nextStatus } : u
      ))
      toast.success(`User ${nextStatus === 'inactive' ? 'deactivated' : 'reactivated'}`)
      setStatusTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status')
    } finally {
      setTogglingStatus(false)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Users"
        subtitle="A single place to manage admin, teacher, student, and parent accounts with the same role-based view used in the mobile admin flow."
        chips={[
          loading ? 'Loading users...' : `${filteredUsers.length} visible`,
          `${roleCounts.teacher || 0} teachers`,
          `${roleCounts.student || 0} students`,
          `${roleCounts.parent || 0} parents`,
        ]}
        actions={
          <>
          <button onClick={fetchData} className="btn-secondary p-2" title="Refresh users">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/bulk-upload')} className="btn-secondary flex items-center gap-2">
            <Upload className="w-4 h-4" /> Bulk Upload
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add User
          </button>
          </>
        }
      />

      <div className="card p-4 space-y-4">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, mobile or admission no..."
            className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {roleOptions.map((role) => (
            <button
              key={role.value}
              onClick={() => setRoleFilter(role.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                roleFilter === role.value
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-primary-200 hover:text-primary-700'
              }`}
            >
              {role.label} <span className="ml-1 opacity-70">({roleCounts[role.value] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="hidden lg:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="table-th">User</th>
                <th className="table-th">Role</th>
                <th className="table-th">Class</th>
                <th className="table-th">Admission No</th>
                <th className="table-th">Contact</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? [...Array(6)].map((_, index) => (
                <tr key={index} className="animate-pulse">
                  {[...Array(6)].map((__, cell) => (
                    <td key={cell} className="table-td">
                      <div className="h-4 bg-gray-100 rounded w-full" />
                    </td>
                  ))}
                </tr>
              )) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-400">No users found for this filter.</td>
                </tr>
              ) : filteredUsers.map((user) => {
                const roleMeta = getRoleMeta(user.role)
                const isInactive = user.accountStatus === 'inactive'
                return (
                  <tr key={getUserId(user)} className={`hover:bg-gray-50 transition-colors ${isInactive ? 'opacity-60' : ''}`}>
                    <td className="table-td">
                      <div className="flex items-center gap-3">
                        <Avatar src={user.avatar} name={getUserName(user)} size="sm" />
                        <div>
                          <button
                            onClick={() => navigate(`/users/${getUserId(user)}`)}
                            className="font-medium text-gray-900 hover:text-primary-700 transition-colors text-left"
                          >
                            {getUserName(user)}
                          </button>
                          <p className="text-xs text-gray-400">{user.email || 'No email added'}</p>
                          {isInactive && <span className="text-xs font-semibold text-red-500">Inactive</span>}
                        </div>
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${roleMeta.badge}`}>
                        {roleMeta.label}
                      </span>
                    </td>
                    <td className="table-td text-gray-600">{user.role === 'student' ? getClassLabel(user) : '—'}</td>
                    <td className="table-td text-gray-600">{user.admissionNo || '—'}</td>
                    <td className="table-td">
                      <div className="space-y-0.5">
                        <p className="text-gray-700">{user.mobile || '—'}</p>
                        <p className="text-xs text-gray-400">{user.gender || '—'}</p>
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/users/${getUserId(user)}`)} className="p-2 rounded-lg text-gray-400 hover:text-primary-700 hover:bg-primary-50 transition-colors" title="View profile">
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEdit(user)} className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setStatusTarget({ id: getUserId(user), name: getUserName(user), currentStatus: user.accountStatus || 'active' })}
                          className={`p-2 rounded-lg transition-colors ${isInactive ? 'text-gray-400 hover:text-green-600 hover:bg-green-50' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                          title={isInactive ? 'Reactivate user' : 'Deactivate user'}
                        >
                          {isInactive ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:hidden">
        {loading ? [...Array(4)].map((_, index) => (
          <div key={index} className="card p-5 animate-pulse space-y-3">
            <div className="h-4 bg-gray-100 rounded w-1/2" />
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-8 bg-gray-100 rounded w-full" />
          </div>
        )) : filteredUsers.length === 0 ? (
          <div className="card p-8 text-center text-gray-400">No users found for this filter.</div>
        ) : filteredUsers.map((user) => {
          const roleMeta = getRoleMeta(user.role)
          const isInactive = user.accountStatus === 'inactive'
          return (
            <div key={getUserId(user)} className={`card p-5 space-y-4 ${isInactive ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar src={user.avatar} name={getUserName(user)} size="lg" />
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate(`/users/${getUserId(user)}`)}
                      className="font-semibold text-gray-900 truncate hover:text-primary-700 transition-colors text-left block"
                    >
                      {getUserName(user)}
                    </button>
                    <p className="text-sm text-gray-500 truncate">{user.email || 'No email added'}</p>
                    {isInactive && <span className="text-xs font-semibold text-red-500">Inactive</span>}
                  </div>
                </div>
                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${roleMeta.badge}`}>
                  {roleMeta.label}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Class</p>
                  <p className="text-gray-700">{user.role === 'student' ? getClassLabel(user) : '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Admission No</p>
                  <p className="text-gray-700">{user.admissionNo || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Mobile</p>
                  <p className="text-gray-700">{user.mobile || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Gender</p>
                  <p className="text-gray-700">{user.gender || '—'}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button onClick={() => navigate(`/users/${getUserId(user)}`)} className="btn-secondary flex-1 flex items-center justify-center gap-1">
                  <ExternalLink className="w-4 h-4" /> Profile
                </button>
                <button onClick={() => openEdit(user)} className="btn-secondary flex-1 flex items-center justify-center gap-1">
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => setStatusTarget({ id: getUserId(user), name: getUserName(user), currentStatus: user.accountStatus || 'active' })}
                  className={`flex-1 rounded-lg border transition-colors flex items-center justify-center gap-1 font-semibold px-4 py-2 ${isInactive ? 'border-green-200 text-green-600 hover:bg-green-50' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                >
                  {isInactive ? <><UserCheck className="w-4 h-4" /> Reactivate</> : <><UserX className="w-4 h-4" /> Deactivate</>}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center">
                  {editingUser?.role === 'admin' || form.role === 'admin'
                    ? <Shield className="w-5 h-5" />
                    : editingUser?.role === 'student' || form.role === 'student'
                    ? <GraduationCap className="w-5 h-5" />
                    : <UsersIcon className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{editingUser ? 'Edit User' : 'Add User'}</h3>
                  <p className="text-xs text-gray-500">{editingUser ? 'Update basic user details' : 'Create a new admin, teacher, student or parent account'}</p>
                </div>
              </div>
              <button onClick={resetModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {!editingUser && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Role</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {roleOptions.filter((item) => item.value !== 'all' && item.value !== 'admin').map((role) => (
                      <button
                        key={role.value}
                        onClick={() => setForm((prev) => ({ ...prev, role: role.value }))}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          form.role === role.value
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-primary-200 hover:text-primary-700'
                        }`}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
                  <input className="input-field w-full" value={form.firstName} onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))} placeholder="First name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Middle Name</label>
                  <input className="input-field w-full" value={form.middleName} onChange={(e) => setForm((prev) => ({ ...prev, middleName: e.target.value }))} placeholder="Optional" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
                  <input className="input-field w-full" value={form.lastName} onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))} placeholder="Last name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                  <input type="email" className="input-field w-full" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="name@school.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Mobile *</label>
                  <input className="input-field w-full" value={form.mobile} onChange={(e) => setForm((prev) => ({ ...prev, mobile: e.target.value }))} placeholder="10-digit Indian mobile" maxLength={10} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Gender</label>
                  <select className="input-field w-full" value={form.gender} onChange={(e) => setForm((prev) => ({ ...prev, gender: e.target.value }))}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth {!editingUser && '*'}</label>
                  <input
                    type="date"
                    className="input-field w-full"
                    value={form.dob}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setForm((prev) => ({ ...prev, dob: e.target.value }))}
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Password *</label>
                    <input type="password" className="input-field w-full" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} placeholder="Min 6 characters" />
                  </div>
                )}
                {!editingUser && form.role === 'student' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Admission No</label>
                      <input className="input-field w-full" value={form.admissionNo} onChange={(e) => setForm((prev) => ({ ...prev, admissionNo: e.target.value }))} placeholder="Optional — auto-generated if blank" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Class *</label>
                      <select className="input-field w-full" value={form.classId} onChange={(e) => setForm((prev) => ({ ...prev, classId: e.target.value }))}>
                        <option value="">Select class</option>
                        {classes.map((cls) => (
                          <option key={cls._id || cls.id} value={cls._id || cls.id}>
                            {cls.name}{cls.section ? ` — ${cls.section}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>


              {editingUser && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  Class and admission number are still managed from the dedicated student screens and relation/timetable flows. This editor is focused on the shared admin user details from the mobile app.
                </div>
              )}
            </div>

            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button onClick={resetModal} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn-primary flex items-center gap-2" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-50' : 'bg-red-50'}`}>
              {statusTarget.currentStatus === 'inactive'
                ? <UserCheck className="w-5 h-5 text-green-500" />
                : <UserX className="w-5 h-5 text-red-500" />
              }
            </div>
            <h3 className="font-bold text-gray-900 mb-1">
              {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'} User?
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {statusTarget.currentStatus === 'inactive'
                ? `${statusTarget.name} will be able to log in again.`
                : `${statusTarget.name} will lose access but data will be preserved.`
              }
            </p>
            <div className="flex gap-3">
              <button onClick={() => setStatusTarget(null)} className="btn-secondary flex-1" disabled={togglingStatus}>Cancel</button>
              <button
                onClick={handleToggleStatus}
                disabled={togglingStatus}
                className={`flex-1 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {togglingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
